import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const benefits = [
  { icon: '🔑', title: 'Access Exclusive Opportunities', desc: 'Many roles are only available through us' },
  { icon: '💡', title: 'Expert Career Guidance', desc: 'Industry specialists who understand your skills' },
  { icon: '⚡', title: 'Fast Process', desc: 'Quick registration and placement' },
  { icon: '💰', title: 'Competitive Pay', desc: 'Weekly pay for temporary workers' },
  { icon: '📈', title: 'Career Development', desc: 'Training and qualification support' },
  { icon: '🤝', title: 'Ongoing Support', desc: "We're here throughout your placement" },
];

const faqs = [
  { q: 'How quickly can I start work?', a: 'Depending on the role and your documentation, we can often place candidates within 24-48 hours for temporary positions.' },
  { q: 'Do I need a CSCS card?', a: "Most construction site work requires a valid CSCS card. If you don't have one, we can guide you through the application process." },
  { q: 'What documentation do I need?', a: "You'll need valid right-to-work documents, relevant trade qualifications, CSCS card (if applicable), and references from previous employers." },
  { q: 'How do I get paid?', a: 'Temporary workers are paid weekly via BACS. Permanent candidates are paid directly by their employer per their agreed salary.' },
  { q: 'Can you help with training?', a: 'Yes! We work with training providers across the UK and can help you access courses, qualifications, and upskilling programmes.' },
  { q: 'What if I have issues on site?', a: 'Contact your dedicated recruitment consultant immediately. We provide 24/7 support for all our temporary workers.' },
];

export default function JobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [page, setPage] = useState(1);
  const [openFaq, setOpenFaq] = useState(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/jobs?page=${page}&limit=12`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (typeFilter) url += `&jobType=${typeFilter}`;
      if (locationFilter) url += `&location=${encodeURIComponent(locationFilter)}`;

      const res = await fetch(url);
      const data = await res.json();

      // Handle both response formats (paginated or legacy)
      if (data.jobs) {
        setJobs(data.jobs);
        setTotal(data.total || data.jobs.length);
      } else if (Array.isArray(data)) {
        setJobs(data);
        setTotal(data.length);
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, locationFilter, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchJobs();
  };

  // Normalise field names (database may use snake_case or camelCase)
  const getField = (job, ...names) => {
    for (const n of names) {
      if (job[n] !== undefined && job[n] !== null) return job[n];
    }
    return null;
  };

  const getJobType = (job) => {
    const t = getField(job, 'jobType', 'job_type', 'employment_type') || 'permanent';
    if (t === 'full-time') return 'permanent';
    return t;
  };

  const getSalary = (job) => {
    const display = getField(job, 'salary', 'salary_display');
    if (display) return display;
    const min = getField(job, 'salaryMin', 'salary_min');
    const max = getField(job, 'salaryMax', 'salary_max');
    if (min && max) return `£${Number(min).toLocaleString()} - £${Number(max).toLocaleString()}`;
    if (min) return `From £${Number(min).toLocaleString()}`;
    if (max) return `Up to £${Number(max).toLocaleString()}`;
    return 'Competitive';
  };

  const getCompany = (job) => getField(job, 'companyName', 'company_name') || getField(job, 'company') || '';
  const getLocation = (job) => getField(job, 'location', 'location_city') || '';
  const getSector = (job) => getField(job, 'sector') || '';

  const locations = [...new Set(jobs.map(j => getLocation(j)).filter(Boolean))];

  return (
    <div>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <h1>Construction Jobs</h1>
          <p>Find your next role in the UK construction industry</p>
          <div className="breadcrumb">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.9)' }}>Jobs</span>
          </div>
        </div>
      </section>

      {/* Search */}
      <section style={{ marginTop: '-30px', position: 'relative', zIndex: 10, padding: '0 20px' }}>
        <div className="container">
          <form className="search-bar" onSubmit={handleSearch}>
            <div className="form-group">
              <label>Job Title or Keyword</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Site Manager, Electrician..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Job Type</label>
              <select className="form-control" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
                <option value="">All Types</option>
                <option value="permanent">Permanent</option>
                <option value="temporary">Temporary</option>
                <option value="contract">Contract</option>
              </select>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. London, Manchester..."
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Search Jobs
            </button>
          </form>
        </div>
      </section>

      {/* Job Listings */}
      <section className="section">
        <div className="container">
          {loading ? (
            <div className="text-center" style={{ padding: '60px 0' }}>
              <p style={{ fontSize: '1.1rem', color: 'var(--gray-600)' }}>Loading jobs...</p>
            </div>
          ) : (
            <>
              <h3 style={{ marginBottom: '25px' }}>
                {total} {total === 1 ? 'Job' : 'Jobs'} Found
              </h3>

              {jobs.length > 0 ? (
                <div className="grid grid-3">
                  {jobs.map((job) => {
                    const jobType = getJobType(job);
                    const badgeClass = jobType === 'permanent' ? 'perm' : jobType === 'temporary' ? 'temp' : 'contract';
                    return (
                      <div className="job-card" key={job.id}>
                        <div className="job-card-header">
                          <h4 className="job-card-title">
                            {job.title}
                            {(job.isFeatured || job.is_featured) ? <span style={{ color: '#ff6b35', marginLeft: '8px' }}>★</span> : null}
                          </h4>
                          <div className="job-card-company">{getCompany(job)}</div>
                        </div>
                        <div className="job-card-meta">
                          <span>📍 {getLocation(job) || 'UK'}</span>
                          <span>
                            <span className={`badge badge-${badgeClass}`}>
                              {jobType.charAt(0).toUpperCase() + jobType.slice(1)}
                            </span>
                          </span>
                          {getSector(job) && <span>🏗️ {getSector(job)}</span>}
                        </div>
                        <div className="job-card-footer">
                          <span className="job-salary">{getSalary(job)}</span>
                          <button className="btn btn-primary btn-sm" onClick={() => navigate('/contact')}>
                            Apply Now
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center" style={{ padding: '60px 0' }}>
                  <p style={{ fontSize: '1.2rem' }}>No jobs found matching your criteria.</p>
                  <p style={{ color: 'var(--gray-600)', marginBottom: '20px' }}>Try adjusting your search or browse all available positions.</p>
                  <button className="btn btn-outline" onClick={() => { setSearch(''); setTypeFilter(''); setLocationFilter(''); setPage(1); }}>
                    Clear Filters
                  </button>
                </div>
              )}

              {/* Pagination */}
              {total > 12 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '30px' }}>
                  <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    ← Previous
                  </button>
                  <span style={{ padding: '10px 20px', color: 'var(--gray-600)' }}>
                    Page {page} of {Math.ceil(total / 12)}
                  </span>
                  <button className="btn btn-outline" disabled={page >= Math.ceil(total / 12)} onClick={() => setPage(p => p + 1)}>
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Why Work With Us?</h2>
            <p>Benefits of registering with Horizon Recruitment</p>
          </div>
          <div className="grid grid-3">
            {benefits.map((b, i) => (
              <div className="feature-item" key={i} style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '25px', boxShadow: 'var(--shadow-sm)' }}>
                <span style={{ fontSize: '2rem' }}>{b.icon}</span>
                <div>
                  <h4>{b.title}</h4>
                  <p style={{ margin: 0 }}>{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Steps */}
      <section className="section-dark">
        <div className="container">
          <div className="section-header">
            <h2>How to Register</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }}>Get started in just a few simple steps</p>
          </div>
          <div className="process-steps">
            {[
              { num: '1', title: 'Register', desc: 'Submit your CV online' },
              { num: '2', title: 'Consultation', desc: 'Initial call with our team' },
              { num: '3', title: 'Verification', desc: 'Skills & document checks' },
              { num: '4', title: 'Placement', desc: 'Get matched to roles' },
            ].map((step, i) => (
              <div className="process-step" key={i}>
                <div className="process-step-number">{step.num}</div>
                <h4 style={{ color: 'var(--white)' }}>{step.title}</h4>
                <p style={{ color: 'rgba(255,255,255,0.7)' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upload CV */}
      <section className="section">
        <div className="container" style={{ maxWidth: '700px' }}>
          <div className="section-header">
            <h2>Upload Your CV</h2>
            <p>Register with us and we'll match you to the best opportunities.</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); alert('Thank you! We will be in touch shortly.'); }}>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" className="form-control" placeholder="Your full name" required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" className="form-control" placeholder="your@email.com" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone *</label>
                <input type="tel" className="form-control" placeholder="Your phone number" required />
              </div>
              <div className="form-group">
                <label>Sector of Interest</label>
                <select className="form-control">
                  <option value="">Select sector...</option>
                  <option>Commercial Construction</option>
                  <option>Residential Development</option>
                  <option>Infrastructure & Civil</option>
                  <option>Industrial & Manufacturing</option>
                  <option>Facilities Management</option>
                  <option>Specialist Trades</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Upload CV</label>
              <input type="file" className="form-control" accept=".pdf,.doc,.docx" />
            </div>
            <div className="form-group">
              <label>Availability</label>
              <select className="form-control">
                <option value="">When can you start?</option>
                <option>Immediately</option>
                <option>Within 1 week</option>
                <option>Within 2 weeks</option>
                <option>Within 1 month</option>
                <option>Negotiable</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }}>
              Submit Application
            </button>
          </form>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-alt">
        <div className="container" style={{ maxWidth: '800px' }}>
          <div className="section-header">
            <h2>Candidate FAQs</h2>
            <p>Common questions from job seekers</p>
          </div>
          {faqs.map((faq, i) => (
            <div className="faq-item" key={i}>
              <button
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                {faq.q}
                <span>{openFaq === i ? '−' : '+'}</span>
              </button>
              {openFaq === i && (
                <div className="faq-answer">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
